/**
 * The single place that turns "what went wrong" (an API error, a network
 * failure, a stray JS exception) into "what to tell a non-technical reader" —
 * plain copy plus one recovery action. Every feedback component (ErrorState,
 * RestrictedState, EngineStatus, AppErrorBoundary) and every ad-hoc
 * `catch`/`onError` in the app should go through {@link toUserError} rather
 * than inventing its own message, so the studio only ever has ONE voice for
 * failure.
 *
 * Hard rule: never surface a raw `detail`/stack/payload. Backend `detail`
 * strings are operator-facing (they can contain validation-error internals,
 * Python exception text, etc.) and are only ever shown verbatim when they
 * match {@link SAFE_DETAIL_PREFIXES} below — a short, hand-reviewed allowlist.
 * Everything else gets a curated, class-level message instead.
 */
import { toast } from "sonner";

import { GraphMindApiError } from "@/lib/api";

export type ErrorActionKind = "retry" | "signin" | "billing" | "support" | "home";

export interface ErrorAction {
  label: string;
  kind: ErrorActionKind;
}

export interface UserError {
  title: string;
  message: string;
  action?: ErrorAction;
}

const RETRY: ErrorAction = { label: "Try again", kind: "retry" };
const SIGN_IN: ErrorAction = { label: "Sign in again", kind: "signin" };
const UPGRADE: ErrorAction = { label: "View plans", kind: "billing" };
const CONTACT: ErrorAction = { label: "Contact support", kind: "support" };
const GO_BACK: ErrorAction = { label: "Go back", kind: "home" };

/** Backend `detail` strings that are already careful, user-appropriate copy
 * (checked by prefix, case-insensitively) — safe to show verbatim instead of
 * the generic per-status message. Every entry here has been read against the
 * server code that raises it (see `server/src/graphmind/billing/service.py`,
 * `domain/billing.py`); none of them can contain a stack trace, a file path,
 * or anything else that shouldn't reach a reader. Extend this list only after
 * doing the same review — never widen it with a broad/generic pattern. */
const SAFE_DETAIL_PREFIXES = [
  "unknown billing item",
  "no stripe price configured",
  "items must include at least one of",
];

function extractDetailText(raw: string): string | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const d = (parsed as { detail?: unknown }).detail;
      if (typeof d === "string") return d;
    }
    return null; // structured but not a plain string detail (e.g. validation errors) — never safe to show raw
  } catch {
    return raw; // not JSON — the body itself is the message
  }
}

function safeDetail(raw: string): string | null {
  const detail = extractDetailText(raw);
  if (!detail) return null;
  const lower = detail.toLowerCase();
  return SAFE_DETAIL_PREFIXES.some((p) => lower.startsWith(p)) ? detail : null;
}

function isAbort(err: unknown): boolean {
  if (err instanceof DOMException) return err.name === "AbortError";
  return err instanceof Error && err.name === "AbortError";
}

/** Client-side timeouts (e.g. LoginPane's auth timeout) throw a plain Error
 * whose message says so — there's no HTTP status to key off. */
function looksLikeTimeout(err: unknown): boolean {
  return err instanceof Error && /\btimed?\s?out\b/i.test(err.message);
}

function fromApiError(err: GraphMindApiError): UserError {
  const detail = safeDetail(err.detail);
  const { status } = err;

  if (status === 401) {
    return {
      title: "Sign-in required",
      message: "Your session has expired. Sign in again to continue.",
      action: SIGN_IN,
    };
  }
  if (status === 402) {
    return {
      title: "Upgrade required",
      message: "You've hit a usage limit on your current plan.",
      action: UPGRADE,
    };
  }
  if (status === 403) {
    return {
      title: "Access denied",
      message: detail ?? "You don't have permission to do that.",
      action: CONTACT,
    };
  }
  if (status === 404) {
    return {
      title: "Not found",
      message: "That item couldn't be found — it may have moved or been deleted.",
      action: GO_BACK,
    };
  }
  if (status === 408) {
    return {
      title: "Timed out",
      message: "That took too long to respond. Please try again.",
      action: RETRY,
    };
  }
  if (status === 429) {
    return {
      title: "Slow down",
      message: "That's a lot of requests at once — wait a moment and try again.",
      action: RETRY,
    };
  }
  if (status === 400 || status === 422) {
    return {
      title: "Couldn't do that",
      message: detail ?? "That request wasn't valid. Double-check and try again.",
      action: RETRY,
    };
  }
  if (status >= 500) {
    return {
      title: "Server problem",
      message: "Something went wrong on our end. Please try again in a moment.",
      action: RETRY,
    };
  }
  return {
    title: "Something went wrong",
    message: "That didn't work as expected. Please try again.",
    action: RETRY,
  };
}

/** Map any caught value into plain, non-technical copy plus (usually) one
 * recovery action. Never throws, never echoes a raw stack/payload. */
export function toUserError(err: unknown): UserError {
  if (err instanceof GraphMindApiError) return fromApiError(err);

  if (isAbort(err)) {
    return { title: "Stopped", message: "That was cancelled before it finished." };
  }

  if (looksLikeTimeout(err)) {
    return {
      title: "Timed out",
      message: "That took too long to respond. Check your connection and try again.",
      action: RETRY,
    };
  }

  // A plain TypeError from a rejected `fetch()` (DNS failure, no connection,
  // CORS) has no HTTP status at all — that's the "offline" case.
  if (err instanceof TypeError) {
    return {
      title: "You're offline",
      message: "Check your internet connection and try again.",
      action: RETRY,
    };
  }

  // Anything else — including an unexpected render/runtime error caught by a
  // boundary. We deliberately never show `err.message` here: it can contain a
  // raw stack frame, a variable dump, or other internals never meant for a
  // reader. Your work is safe (local-first storage + autosave) regardless.
  return {
    title: "Something went wrong",
    message: "Your work is safe — try again, and reach out if this keeps happening.",
    action: RETRY,
  };
}

/** Sonner toast for a caught error, using the same curated copy as every
 * other failure surface. Pass `onRetry` to also offer a one-click retry
 * directly on the toast when the mapped action is retryable. */
export function notifyError(err: unknown, opts?: { onRetry?: () => void }): void {
  const { title, message, action } = toUserError(err);
  toast.error(title, {
    description: message,
    action:
      action?.kind === "retry" && opts?.onRetry
        ? { label: action.label, onClick: opts.onRetry }
        : undefined,
  });
}
